type PromptList = {
  results?: Array<{ id: number; name?: string; tags?: any[] }>;
};

function getPromptList(
  prompts: PromptList | Array<{ id: number; name?: string }> | undefined
) {
  if (!prompts) return undefined;
  return Array.isArray(prompts) ? prompts : prompts.results;
}

export const getPromptIdByName = (
  prompts: PromptList | Array<{ id: number; name?: string }> | undefined,
  name: string
) => {
  const list = getPromptList(prompts);
  if (!list || !name) return undefined;

  return list.find(
    (prompt) => prompt.name?.toLowerCase() === name.toLowerCase()
  );
};

export const getPromptId = (prompts: PromptList | undefined) => {
  const list = prompts?.results;
  if (!list) return undefined;

  return list.find((prompt) =>
    prompt.tags?.some(
      (tag: { value?: string }) => tag.value?.toLowerCase() === 'coach'
    )
  );
};

const RESERVED_VARIABLE_PREFIXES = [
  'user.',
  'shared.',
  'system.',
  'tenant.',
  'cohort.',
] as const;

export const VALID_VARIABLE_PREFIXES = [
  'prompt',
  'shared',
  'user',
  'tenant',
  'cohort',
] as const;

const VALID_VARIABLE_PREFIX_EXAMPLES = VALID_VARIABLE_PREFIXES.map(
  (prefix) => `${prefix}.example`
).join(', ');

// Helper function to check if a variable key is reserved
export const isReservedVariable = (key: string): boolean =>
  RESERVED_VARIABLE_PREFIXES.some((prefix) => key.startsWith(prefix));

export const TENANT_VARIABLE_PREFIX = 'tenant.';

export function stripTenantVariablePrefix(title: string): string {
  if (title.toLowerCase().startsWith(TENANT_VARIABLE_PREFIX)) {
    return title.slice(TENANT_VARIABLE_PREFIX.length);
  }

  return title;
}

export function toTenantVariableTitle(name: string): string {
  return `${TENANT_VARIABLE_PREFIX}${stripTenantVariablePrefix(name)}`;
}

export const COHORT_VARIABLE_PREFIX = 'cohort.';

export function stripCohortVariablePrefix(name: string): string {
  if (name.toLowerCase().startsWith(COHORT_VARIABLE_PREFIX)) {
    return name.slice(COHORT_VARIABLE_PREFIX.length);
  }

  return name;
}

export function validateTenantVariableTitle(name: string): {
  isValid: boolean;
  errorMessage?: string;
} {
  if (!name.trim()) {
    return { isValid: false, errorMessage: 'Variable name is required' };
  }

  if (/\s/.test(name)) {
    return {
      isValid: false,
      errorMessage:
        'Variable name cannot contain spaces. Use underscores between words (e.g. company_name).',
    };
  }

  if (/\./.test(name)) {
    return {
      isValid: false,
      errorMessage:
        'Variable name cannot contain dots. Use underscores between words (e.g. company_name).',
    };
  }

  if (!/^[a-zA-Z0-9_]+$/.test(name)) {
    return {
      isValid: false,
      errorMessage:
        'Use only letters, numbers, and underscores (e.g. company_name, support_email).',
    };
  }

  return { isValid: true };
}

export function validateTenantVariableTitleUnique(
  name: string,
  existingTitles: Iterable<string>,
  editingTitle?: string | null
): { isValid: boolean; errorMessage?: string } {
  const normalized = toTenantVariableTitle(name);
  const normalizedEditing = editingTitle
    ? toTenantVariableTitle(stripTenantVariablePrefix(editingTitle))
    : null;

  if (normalizedEditing && normalized === normalizedEditing) {
    return { isValid: true };
  }

  const existing = new Set(
    [...existingTitles].map((title) =>
      toTenantVariableTitle(stripTenantVariablePrefix(title))
    )
  );

  if (existing.has(normalized)) {
    return {
      isValid: false,
      errorMessage: 'This variable has already been created.',
    };
  }

  return { isValid: true };
}

export function validatePlainVariableNameUnique(
  name: string,
  existingNames: Iterable<string>,
  editingName?: string | null
): { isValid: boolean; errorMessage?: string } {
  const normalized = name.trim().toLowerCase();
  const normalizedEditing = editingName?.trim().toLowerCase() ?? null;

  if (normalizedEditing && normalized === normalizedEditing) {
    return { isValid: true };
  }

  const existing = new Set(
    [...existingNames].map((entry) => entry.trim().toLowerCase())
  );

  if (existing.has(normalized)) {
    return {
      isValid: false,
      errorMessage: 'This variable has already been created.',
    };
  }

  return { isValid: true };
}

export function validateCohortVariableNameUnique(
  name: string,
  existingNames: Iterable<string>,
  editingName?: string | null
): { isValid: boolean; errorMessage?: string } {
  const normalized = stripCohortVariablePrefix(name).trim().toLowerCase();
  const normalizedEditing = editingName
    ? stripCohortVariablePrefix(editingName).trim().toLowerCase()
    : null;

  if (normalizedEditing && normalized === normalizedEditing) {
    return { isValid: true };
  }

  const existing = new Set(
    [...existingNames].map((entry) =>
      stripCohortVariablePrefix(entry).trim().toLowerCase()
    )
  );

  if (existing.has(normalized)) {
    return {
      isValid: false,
      errorMessage: 'This variable has already been created.',
    };
  }

  return { isValid: true };
}

export function validateVariableName(variableName: string): {
  isValid: boolean;
  errorMessage?: string;
} {
  const hasValidPrefix = VALID_VARIABLE_PREFIXES.some((prefix) => {
    const prefixPattern = new RegExp(`^${prefix}\\.`, 'i');
    return prefixPattern.test(variableName);
  });

  if (!hasValidPrefix) {
    return {
      isValid: false,
      errorMessage: `Variable "${variableName}" must follow the format: validPrefix.{variable} (e.g., ${VALID_VARIABLE_PREFIX_EXAMPLES})`,
    };
  }

  const parts = variableName.split('.');
  if (parts.length !== 2 || !parts[1] || parts[1].trim() === '') {
    return {
      isValid: false,
      errorMessage: `Variable "${variableName}" must have a variable name after the dot (e.g., prompt.title)`,
    };
  }

  return { isValid: true };
}

export interface PromptVariableParseResult {
  variables: string[];
  syntaxErrors: string[];
}

/**
 * Scan for {{variable}} placeholders left-to-right.
 * Unlike a single regex, this detects unclosed/nested {{ and still extracts
 * valid placeholders that appear after malformed ones.
 */
export function parsePromptVariables(
  content: string
): PromptVariableParseResult {
  const variables: string[] = [];
  const syntaxErrors: string[] = [];
  let index = 0;

  while (index < content.length) {
    const openIndex = content.indexOf('{{', index);
    if (openIndex === -1) break;

    const closeIndex = content.indexOf('}}', openIndex + 2);
    if (closeIndex === -1) {
      const snippet = content
        .slice(openIndex, openIndex + 48)
        .replace(/\s+/g, ' ');
      syntaxErrors.push(
        `Unclosed placeholder starting with "${snippet.trim()}…" — add a closing }}`
      );
      break;
    }

    const inner = content.slice(openIndex + 2, closeIndex);

    if (inner.includes('{{')) {
      const preview = inner.replace(/\s+/g, ' ').trim().slice(0, 72);
      syntaxErrors.push(
        `Malformed placeholder "{{${preview}}}…" — remove the extra {{ or close an earlier placeholder before this one`
      );
      index = closeIndex + 2;
      continue;
    }

    const name = inner.trim();
    if (!name) {
      syntaxErrors.push(
        'Empty variable placeholder {{}} — provide a variable name'
      );
    } else {
      variables.push(name);
    }

    index = closeIndex + 2;
  }

  return { variables, syntaxErrors };
}

/** Validate {{variable}} syntax and prefix rules in prompt content. */
export function validatePromptVariables(promptContent: string): {
  isValid: boolean;
  errors: string[];
} {
  const { variables, syntaxErrors } = parsePromptVariables(promptContent);
  const errors = [...syntaxErrors];
  const seen = new Set<string>();

  for (const variableName of variables) {
    if (seen.has(variableName)) continue;
    seen.add(variableName);

    const validation = validateVariableName(variableName);
    if (!validation.isValid && validation.errorMessage) {
      errors.push(validation.errorMessage);
    }
  }

  return {
    isValid: errors.length === 0,
    errors,
  };
}

// The previous prompt editor saved some characters as HTML codes (e.g. "&"
// as "&amp;"), and those codes reached the model verbatim. Only the codes it
// produced are listed; any other text, such as "R&D" or "&&", is untouched.
const LEGACY_HTML_ENTITIES: Record<string, string> = {
  '&amp;': '&',
  '&lt;': '<',
  '&gt;': '>',
  '&quot;': '"',
  '&#39;': "'",
  '&#x27;': "'",
  '&#x2f;': '/',
  '&nbsp;': ' ',
  '\u00a0': ' ',
};

const LEGACY_HTML_ENTITY_PATTERN =
  /&(?:amp|lt|gt|quot|nbsp|#39|#x27|#x2[fF]);|\u00a0/g;

/**
 * Replace legacy HTML codes with the characters they stand for, applied when a
 * prompt is saved. Each code is decoded once: "&amp;lt;" becomes "&lt;".
 */
export function decodeLegacyHtmlEntities(prompt: string): string {
  return prompt.replace(
    LEGACY_HTML_ENTITY_PATTERN,
    (code) => LEGACY_HTML_ENTITIES[code.toLowerCase()] ?? code
  );
}

// Markdown the prompt editor stores (see prompt-editor/prompt-markdown.ts),
// matched the same way so previews strip exactly what the editor formats.
const MARKDOWN_BLOCK_PREFIX =
  /^(?:#{1,3}[ \t]+|>[ \t]+|(?: {2})*-[ \t]+|(?: {2})*\d{1,9}\.[ \t]+)/;
const MARKDOWN_TABLE_ROW = /^\|(.*)\|$/;
const MARKDOWN_INLINE_PATTERNS = [
  /()`([^`]+)`/g,
  /()\*\*(\S(?:.*?\S)??)\*\*/g,
  /()<u>(.+?)<\/u>/g,
  /(^|[^*])\*([^\s*](?:.*?[^\s*])??)\*(?!\*)/g,
  /(^|[^_\w])_([^\s_](?:.*?[^\s_])??)_(?![_\w])/g,
];

const stripInlineMarkdown = (line: string): string => {
  let text = line;
  // A few passes unwrap nested formatting such as **_text_**
  for (let pass = 0; pass < 3; pass += 1) {
    const before = text;
    MARKDOWN_INLINE_PATTERNS.forEach((pattern) => {
      text = text.replace(pattern, (_match, prefix, inner) => prefix + inner);
    });
    if (text === before) break;
  }
  return text;
};

/**
 * One-line plain-text preview of a stored prompt for lists and cards: no
 * Markdown markers or table pipes, no legacy HTML codes, lines joined with
 * spaces. For display only; never use it for the text that is saved.
 */
export function promptToPlainText(prompt: string): string {
  return decodeLegacyHtmlEntities(prompt ?? '')
    .split(/\r?\n/)
    .map((line) => {
      const table = line.trim().match(MARKDOWN_TABLE_ROW);
      if (table) {
        // "\\|" is a pipe inside a cell, not a cell boundary
        const cells = table[1]
          .replace(/\\\|/g, '\uE000')
          .split('|')
          .map((cell) => cell.replace(/\uE000/g, '|').trim());
        // A "| --- | --- |" separator row has no text
        if (cells.every((cell) => /^:?-*:?$/.test(cell))) return '';
        return stripInlineMarkdown(cells.filter(Boolean).join(' '));
      }
      return stripInlineMarkdown(line.replace(MARKDOWN_BLOCK_PREFIX, ''));
    })
    .filter((line) => line.trim())
    .join(' ')
    .replace(/\s+/g, ' ')
    .trim();
}

/** @deprecated Use parsePromptVariables — kept for callers that only need names. */
export function extractVariablesFromPrompt(promptContent: string): string[] {
  return parsePromptVariables(promptContent).variables;
}

/** Parse undeclared/invalid variable errors from prompt PATCH 400 responses. */
export function parsePromptSystemVariableErrors(error: unknown): string[] {
  const data = (error as { response?: { data?: { system_prompt?: unknown } } })
    ?.response?.data;

  if (!data?.system_prompt || typeof data.system_prompt !== 'object') {
    return [];
  }

  return Object.keys(data.system_prompt as Record<string, unknown>);
}
